import {t} from 'i18next';

export type CreateIndexPayload = {
  indexName: string;
  savedSearchId: string;
  sourceIds: string[];
  emailRecipients?: string[];
};

export type IndexService = {
  createIndex: (payload: CreateIndexPayload) => Promise<void>;
};

export type RawTreeNode = {
  id: string;
  label: string;
  docCount?: number | string;
  children?: RawTreeNode[];
};

export type ArrTreeItem = {
  id: string;
  label: string;
  docCount?: number;
  children?: ArrTreeItem[];
};

export type Dataset = {
  id: string;
  label: string;
  type?: string;
  disabled?: boolean;
  key?: string[];
  data: RawTreeNode[];
};

export type EditIndexDetails = {
  indexName: string;
  indexId?: string | number;
};

export const MAX_INDEX_NAME = 200;
export const EMAIL_REGEX = /^[^\s@]{1,64}@[^\s@]{1,255}\.[^\s@]{2,63}$/i;

export const normalizeEmail = (email: string) => email.trim().toLowerCase();

export const validateEmail = (email: string) => {
  const e = normalizeEmail(email);
  if (!EMAIL_REGEX.test(e))
    return t('ai.investigate.createEditIndex.create.emailError');
  return null;
};
export const validateIndexName = (name: string | null) => {
  const v = (name ?? '').trim();
  if (!v) return t('ai.investigate.createEditIndex.create.inputreqerror');
  if (v.length > MAX_INDEX_NAME)
    return `${t('ai.investigate.createEditIndex.create.inputSupportText')}: ${MAX_INDEX_NAME}`;

  const indexNameRegex = /^[a-zA-Z0-9 _-]+$/;
  if (!indexNameRegex.test(v))
    return t('ai.investigate.createEditIndex.create.indexNameSpecialChars');

  return null;
};
export const validateSavedSearch = (id: string) => {
  return null;
};
export const validateSourceIds = (ids: string[]) => {
  return ids.length == 0;
};

export const toNumber = (n: unknown) => {
  if (typeof n === 'number') return Number.isFinite(n) ? n : 0;
  if (typeof n === 'string') {
    const v = Number(n);
    return Number.isFinite(v) ? v : 0;
  }
  return 0;
};

export const normalizeNodes = (items: RawTreeNode[]) => {
  const walk = (arr: RawTreeNode[]): ArrTreeItem[] =>
    arr.map((n) => ({
      id: n.id,
      label: n.label,
      docCount: toNumber(n.docCount ?? 0),
      children: n.children ? walk(n.children) : undefined,
    }));
  return walk(items);
};

export const filterTree = (nodes: ArrTreeItem[], query: string) => {
  if (!query) return nodes;
  const q = query.toLowerCase();
  const walk = (arr: ArrTreeItem[]): ArrTreeItem[] => {
    const out: ArrTreeItem[] = [];
    for (const n of arr) {
      const match = n.label.toLowerCase().includes(q);
      const kids = n.children ? walk(n.children) : [];
      if (match || kids.length) {
        out.push({...n, children: kids.length ? kids : n.children});
      }
    }
    return out;
  };
  return walk(nodes);
};

export const buildIdMap = (nodes: ArrTreeItem[]) => {
  const map = new Map<string, ArrTreeItem>();
  const stack = [...nodes];
  while (stack.length) {
    const cur = stack.pop()!;
    map.set(cur.id, cur);
    if (cur.children?.length) stack.push(...cur.children);
  }
  return map;
};

export const getLeafNodes = (node: ArrTreeItem) => {
  if (!node.children || node.children.length === 0) return [node];
  const out: ArrTreeItem[] = [];
  const st = [...node.children];
  while (st.length) {
    const cur = st.pop()!;
    if (cur.children?.length) st.push(...cur.children);
    else out.push(cur);
  }
  return out;
};

export const labelsAndCountFromSelection = (
  selectedIds: string[],
  idMap: Map<string, ArrTreeItem>
) => {
  const labels: string[] = [];
  const seen = new Set<string>();
  let total = 0;

  const collectLeavesInOrder = (node: ArrTreeItem): ArrTreeItem[] => {
    if (!node.children || node.children.length === 0) return [node];
    const out: ArrTreeItem[] = [];
    for (const child of node.children) {
      const childNode = idMap.get(child.id) ?? child;
      out.push(...collectLeavesInOrder(childNode));
    }
    return out;
  };

  for (const id of selectedIds) {
    const node = idMap.get(id);
    if (!node) continue;

    if (node.children && node.children.length > 0) {
      const leaves = collectLeavesInOrder(node);
      for (const leaf of leaves) {
        if (!seen.has(leaf.label)) {
          seen.add(leaf.label);
          labels.push(leaf.label);
        }
      }
    } else {
      if (!seen.has(node.label)) {
        seen.add(node.label);
        labels.push(node.label);
      }
      total += node.docCount ?? 0;
    }
  }

  return {labels, totalCount: total};
};

export const addremoveItems = [
  {
    id: 'add',
    value: 'ADD',
    label: t('ai.investigate.createEditIndex.edit.adddoc'),
  },
  {
    id: 'remove',
    value: 'REMOVE',
    label: t('ai.investigate.createEditIndex.edit.removedoc'),
  },
];

export const getParentIds = <T extends {id: string; children?: T[]}>(
  nodes: T[]
): string[] => {
  const parentIds: string[] = [];

  const walk = (list: T[]) => {
    for (const node of list) {
      if (node.children?.length) {
        parentIds.push(node.id);
        walk(node.children);
      }
    }
  };

  walk(nodes);
  return parentIds;
};

export const IndexDataSet = [
  {
    id: 'Saved Search',
    label: 'Saved Search',
    type: 'multi',
    disabled: false,
    key: ['TC'],
    data: [
      {
        id: 'TC-4',
        label: 'Technical Issue',
        docCount: '10',
      },
      {
        id: 'TC-3',
        label: 'Private Data',
        docCount: '10',
      },
      {
        id: 'TC-2',
        label: 'Privileged',
        docCount: '10',
      },
      {
        id: 'TC-1',
        label: 'Responsive',
        docCount: '10',
      },
    ],
  },
  {
    id: 'tags',
    label: 'Tags',
    type: 'multi',
    disabled: false,
    key: ['TG'],
    data: [
      {
        id: 'TG-1',
        label: 'All Tags',
        children: [
          {
            id: 'TG398',
            label: 'tes1',
            docCount: '10',
          },
          {
            id: 'TG393',
            label: 'Uzi',
            docCount: '10',
          },
        ],
      },
    ],
  },
  {
    id: 'folders',
    label: 'Folders',
    type: 'multi',
    disabled: false,
    key: ['AG'],
    data: [
      {
        id: 'AG-1',
        label: 'Root',
        children: [
          {
            id: 'AG4',
            label: 'QuickBatch2',
            docCount: '10',
          },
          {
            id: 'AG12',
            label: 'Assignment18Oct25',
            docCount: '10',
          },
          {
            id: 'AG13',
            label: 'ASGN2Oct8',
            docCount: '10',
          },
          {
            id: 'AG14',
            label: 'Test',
          },
          {
            id: 'AG15',
            label: 'JLtest1022',
          },
          {
            id: 'AG16',
            label: 'testak1',
          },
          {
            id: 'AG17',
            label: 'ArrAssign24Oct01',
          },
          {
            id: 'AG18',
            label: 'ArrAssign24Oct02',
          },
          {
            id: 'AG19',
            label: 'ArrAssign24Oct03',
          },
          {
            id: 'AG20',
            label: 'ArrAssign24Oct05',
          },
          {
            id: 'AG21',
            label: 'JLAssignmentGroupCasOff',
            children: [
              {
                id: 'AG22',
                label: 'TestGroupCasOn',
                children: [
                  {
                    id: 'AG23',
                    label: 'NestedTestGroupCasOff',
                    children: [
                      {
                        id: 'AG752',
                        label: 'Test12',
                      },
                    ],
                  },
                  {
                    id: 'AG26',
                    label: 'testjen1029',
                  },
                  {
                    id: 'AG57',
                    label: 'keyword',
                    children: [
                      {
                        id: 'AG82',
                        label: '1AssignmentGroup',
                        children: [
                          {
                            id: 'AG421',
                            label: '2ndfrombulkassignwithcasacdeenabled',
                          },
                          {
                            id: 'AG422',
                            label: 'newassignemnet 2sd',
                          },
                          {
                            id: 'AG425',
                            label: 'BulkAssignmentformdocexporerwithassignid',
                          },
                          {
                            id: 'AG427',
                            label: '3rdbulkassign',
                          },
                          {
                            id: 'AG434',
                            label: 'NewAssignemntbulkassignmodechk2',
                          },
                          {
                            id: 'AG516',
                            label: 'FolderDocsFlowChk',
                          },
                          {
                            id: 'AG765',
                            label: 'New Test Assignment',
                          },
                          {
                            id: 'AG767',
                            label: 'Test123',
                          },
                          {
                            id: 'AG769',
                            label: 'asd',
                          },
                        ],
                      },
                    ],
                  },
                  {
                    id: 'AG91',
                    label: 'jl124',
                  },
                  {
                    id: 'AG747',
                    label: 'Testing 1',
                  },
                ],
              },
              {
                id: 'AG34',
                label: 'ABC test',
              },
              {
                id: 'AG92',
                label: 'jen124new',
              },
              {
                id: 'AG443',
                label: 'Assignbulk4058chk',
              },
              {
                id: 'AG450',
                label: 'asignbulk',
              },
              {
                id: 'AG727',
                label: 'NewAssignfrondc',
              },
              {
                id: 'AG772',
                label: 'jlbulkassign24',
              },
              {
                id: 'AG773',
                label: 'assignment0502',
              },
              {
                id: 'AG774',
                label: 'assignment95022',
              },
            ],
          },
          {
            id: 'AG27',
            label: 'Assignment12344',
          },
          {
            id: 'AG35',
            label: 'ASGN12Nov',
          },
          {
            id: 'AG40',
            label: 'Copy 40 Copy 5 Assignment1',
          },
          {
            id: 'AG43',
            label: 'abc tes3',
          },
          {
            id: 'AG58',
            label: 'test22',
          },
          {
            id: 'AG60',
            label: 'a7',
          },
          {
            id: 'AG61',
            label: 'TestAssignment21Nov',
          },
          {
            id: 'AG63',
            label: 'Test1',
          },
          {
            id: 'AG79',
            label: 'Test4',
          },
          {
            id: 'AG80',
            label: 'teststst',
          },
          {
            id: 'AG83',
            label: 'Test122',
          },
          {
            id: 'AG84',
            label: 'Copy 84 JLtest1022',
          },
          {
            id: 'AG85',
            label: 'Copy 85 JLtest1022',
          },
          {
            id: 'AG87',
            label: 'Ne Hemant Quick Batch',
          },
          {
            id: 'AG88',
            label: 'JLtest124',
          },
          {
            id: 'AG89',
            label: 'Copy 89 testak1',
          },
          {
            id: 'AG99',
            label: 'Copy 99 JLtest1022',
          },
          {
            id: 'AG105',
            label: 'jlquickbatch1210',
          },
          {
            id: 'AG140',
            label: 'HirenAssign',
          },
          {
            id: 'AG263',
            label: 'Test25121',
          },
          {
            id: 'AG267',
            label: 'N QB 1',
          },
          {
            id: 'AG271',
            label: 'sa',
          },
          {
            id: 'AG291',
            label: 'HTest',
          },
          {
            id: 'AG293',
            label: 'HHH',
          },
          {
            id: 'AG297',
            label: 'QHD',
          },
          {
            id: 'AG299',
            label: 'aa1',
          },
          {
            id: 'AG301',
            label: 'N QB 2',
          },
          {
            id: 'AG304',
            label: 'Hiren19dec',
          },
          {
            id: 'AG319',
            label: 'Sujata23dec',
          },
          {
            id: 'AG339',
            label: 'assign135',
          },
          {
            id: 'AG342',
            label: 'NQB23Dec',
          },
          {
            id: 'AG343',
            label: 'HirenAssign24Dec',
          },
          {
            id: 'AG346',
            label: 'Sujata2755',
          },
          {
            id: 'AG347',
            label: 'Sujata24dec',
          },
          {
            id: 'AG354',
            label: 'testTimelineAssign29Dec',
          },
          {
            id: 'AG356',
            label: 'testTimeline29New',
          },
          {
            id: 'AG359',
            label: 'AssigSelv101',
          },
          {
            id: 'AG361',
            label: 'LL',
          },
          {
            id: 'AG365',
            label: 's1',
          },
          {
            id: 'AG376',
            label: 's3',
          },
          {
            id: 'AG382',
            label: 'AssignSelv102',
          },
          {
            id: 'AG395',
            label: 's4',
          },
          {
            id: 'AG396',
            label: 's5',
          },
          {
            id: 'AG397',
            label: 's6',
          },
          {
            id: 'AG401',
            label: 's7',
          },
          {
            id: 'AG402',
            label: 's9',
          },
          {
            id: 'AG406',
            label: 'TestAssignement',
          },
          {
            id: 'AG408',
            label: 's8',
          },
          {
            id: 'AG411',
            label: 'NTest2',
          },
          {
            id: 'AG416',
            label: 'NTest4',
          },
          {
            id: 'AG418',
            label: 's10',
          },
          {
            id: 'AG424',
            label: 's11',
          },
          {
            id: 'AG436',
            label: 's12',
          },
          {
            id: 'AG438',
            label: 'bulkassignenewassignmentflow',
          },
          {
            id: 'AG444',
            label: 'Afrantest',
          },
          {
            id: 'AG446',
            label: 'qabulkassign',
          },
          {
            id: 'AG448',
            label: 'ProgressBarTest',
          },
          {
            id: 'AG451',
            label: 'CSon',
            children: [
              {
                id: 'AG348',
                label: 'Sujata24DecTech',
              },
              {
                id: 'AG454',
                label: 'Test Jan2 cson',
              },
              {
                id: 'AG462',
                label: 'TestCsonnnnnnn',
              },
              {
                id: 'AG464',
                label: 'TestCson ON',
              },
              {
                id: 'AG640',
                label: 'cson testt',
              },
              {
                id: 'AG652',
                label: 'test27',
              },
              {
                id: 'AG776',
                label: 'hd9feb',
              },
            ],
          },
          {
            id: 'AG456',
            label: 'csoff',
            children: [
              {
                id: 'AG42',
                label: 'senior',
                children: [
                  {
                    id: 'AG55',
                    label: 'junior',
                    children: [
                      {
                        id: 'AG90',
                        label: 'Dec12JL',
                        children: [
                          {
                            id: 'AG95',
                            label: 'jenassign',
                          },
                        ],
                      },
                      {
                        id: 'AG770',
                        label: 'new test assing',
                      },
                    ],
                  },
                  {
                    id: 'AG94',
                    label: 'jltest128',
                    children: [
                      {
                        id: 'AG305',
                        label: 'Sujata19Dec',
                        children: [
                          {
                            id: 'AG313',
                            label: 'TestBulkAssign22Dec',
                            children: [
                              {
                                id: 'AG442',
                                label: 'assignbulkfloew',
                              },
                            ],
                          },
                          {
                            id: 'AG344',
                            label: 'AmanGroup',
                            children: [
                              {
                                id: 'AG345',
                                label: 'TestAssgn01',
                              },
                              {
                                id: 'AG417',
                                label: 'NewAssignemnetformBulkAssign1st',
                              },
                              {
                                id: 'AG419',
                                label: 'newassignment form assignementgrid',
                              },
                              {
                                id: 'AG430',
                                label: 'NewAssignmentbulkaction',
                              },
                              {
                                id: 'AG432',
                                label: 'normalassignmentmodechk',
                              },
                              {
                                id: 'AG433',
                                label: 'finalchkbulkassign',
                              },
                              {
                                id: 'AG441',
                                label: 'NewAssignemntCreationfromBA',
                              },
                            ],
                          },
                        ],
                      },
                    ],
                  },
                ],
              },
              {
                id: 'AG457',
                label: 'Csoff assignment',
              },
              {
                id: 'AG467',
                label: 'TestCSoffff OFF',
              },
              {
                id: 'AG483',
                label: 'TestAryan',
              },
              {
                id: 'AG486',
                label: 'Testaryan9177',
              },
              {
                id: 'AG589',
                label: 'Aryan9177second',
              },
            ],
          },
          {
            id: 'AG461',
            label: 's13',
          },
          {
            id: 'AG463',
            label: 's14',
          },
          {
            id: 'AG465',
            label: 'Test11',
          },
          {
            id: 'AG470',
            label: 'sa2',
          },
          {
            id: 'AG471',
            label: 'sa4',
          },
          {
            id: 'AG472',
            label: 'ss',
          },
          {
            id: 'AG474',
            label: 'ss1',
          },
          {
            id: 'AG477',
            label: 'sa5',
          },
          {
            id: 'AG480',
            label: 'sa6',
          },
          {
            id: 'AG482',
            label: 'Testing Atryan',
          },
          {
            id: 'AG489',
            label: 'sa7',
          },
          {
            id: 'AG491',
            label: 'sa9',
          },
          {
            id: 'AG494',
            label: 'fsfsadf',
          },
          {
            id: 'AG502',
            label: 'ff',
          },
          {
            id: 'AG504',
            label: 'sa10',
          },
          {
            id: 'AG506',
            label: 'Ha2',
          },
          {
            id: 'AG511',
            label: 'Ha',
          },
          {
            id: 'AG525',
            label: 'testdec',
          },
          {
            id: 'AG528',
            label: 'Abhay Assignment',
          },
          {
            id: 'AG538',
            label: 'TestV5',
          },
          {
            id: 'AG548',
            label: 'Ha3',
          },
          {
            id: 'AG549',
            label: 'Testv6',
          },
          {
            id: 'AG557',
            label: 'TestV7',
          },
          {
            id: 'AG560',
            label: 'Testv8',
          },
          {
            id: 'AG566',
            label: 'ha4',
          },
          {
            id: 'AG567',
            label: 'HA5',
          },
          {
            id: 'AG570',
            label: 'aa3',
          },
          {
            id: 'AG571',
            label: 'aa4',
          },
          {
            id: 'AG573',
            label: 'vjtesting1',
          },
          {
            id: 'AG587',
            label: 'vjtesting2',
          },
          {
            id: 'AG592',
            label: 'vjtesting3',
          },
          {
            id: 'AG598',
            label: 'nn',
          },
          {
            id: 'AG599',
            label: 'assign789',
          },
          {
            id: 'AG600',
            label: 'jj',
          },
          {
            id: 'AG601',
            label: 'Test Jan12',
          },
          {
            id: 'AG603',
            label: 'ndn',
          },
          {
            id: 'AG604',
            label: 'testa1',
          },
          {
            id: 'AG605',
            label: 'PSAssignment',
          },
          {
            id: 'AG606',
            label: 'ps2',
          },
          {
            id: 'AG607',
            label: 'ps3',
          },
          {
            id: 'AG609',
            label: 'ps4',
          },
          {
            id: 'AG611',
            label: 'PS5',
          },
          {
            id: 'AG612',
            label: 'testv1',
          },
          {
            id: 'AG615',
            label: 'PS6',
          },
          {
            id: 'AG616',
            label: 'TEST3',
          },
          {
            id: 'AG618',
            label: 'RRR',
          },
          {
            id: 'AG620',
            label: 'vjtesting5',
          },
          {
            id: 'AG621',
            label: 'vjtesting6',
          },
          {
            id: 'AG622',
            label: 'vjtesting7',
          },
          {
            id: 'AG623',
            label: 'vjtesting8',
          },
          {
            id: 'AG624',
            label: 'vjtesting9',
          },
          {
            id: 'AG625',
            label: 'vjtesting10',
          },
          {
            id: 'AG626',
            label: 'vjtesting11',
          },
          {
            id: 'AG637',
            label: 'TT',
          },
          {
            id: 'AG639',
            label: 'cson test',
          },
          {
            id: 'AG642',
            label: 'cson check',
          },
          {
            id: 'AG644',
            label: 'NAssign',
          },
          {
            id: 'AG646',
            label: 'CNewq',
          },
          {
            id: 'AG648',
            label: 'W3',
          },
          {
            id: 'AG651',
            label: 'NightCity2077',
          },
          {
            id: 'AG653',
            label: 'test271',
          },
          {
            id: 'AG655',
            label: 'NB01',
          },
          {
            id: 'AG656',
            label: 'VFNC',
          },
          {
            id: 'AG657',
            label: 'Batch 1',
          },
          {
            id: 'AG658',
            label: 'Quick Batch 2',
          },
          {
            id: 'AG659',
            label: 'QB1',
          },
          {
            id: 'AG660',
            label: 'QB2',
          },
          {
            id: 'AG661',
            label: 'QB3',
          },
          {
            id: 'AG662',
            label: 'QB4',
          },
          {
            id: 'AG663',
            label: 'qb5',
          },
          {
            id: 'AG664',
            label: 'qb6',
          },
          {
            id: 'AG665',
            label: 'Testing2',
          },
          {
            id: 'AG666',
            label: 'test272',
          },
          {
            id: 'AG667',
            label: 'test273',
          },
          {
            id: 'AG668',
            label: 'test274',
          },
          {
            id: 'AG671',
            label: 'test275',
          },
          {
            id: 'AG673',
            label: 'test279',
          },
          {
            id: 'AG675',
            label: 'test280',
          },
          {
            id: 'AG676',
            label: 'Testing3Assignment',
          },
          {
            id: 'AG678',
            label: 'test281',
          },
          {
            id: 'AG679',
            label: 'Testing4Assignment',
          },
          {
            id: 'AG681',
            label: 'FFX',
          },
          {
            id: 'AG690',
            label: 'G of Rivia',
          },
          {
            id: 'AG694',
            label: 'Newthing',
          },
          {
            id: 'AG696',
            label: 'SD',
          },
          {
            id: 'AG698',
            label: 'S',
          },
          {
            id: 'AG704',
            label: 'A11',
          },
          {
            id: 'AG709',
            label: 'Night City 2077',
          },
          {
            id: 'AG712',
            label: 'test283',
          },
          {
            id: 'AG713',
            label: 'test285',
          },
          {
            id: 'AG715',
            label: 'test300',
          },
          {
            id: 'AG721',
            label: 'NAs77',
          },
          {
            id: 'AG728',
            label: 'RootAssign001',
          },
          {
            id: 'AG729',
            label: 'AB729',
          },
          {
            id: 'AG732',
            label: 'SM1',
          },
          {
            id: 'AG733',
            label: 'AB17',
          },
          {
            id: 'AG734',
            label: 'VK18',
          },
          {
            id: 'AG735',
            label: 'HP33',
          },
          {
            id: 'AG736',
            label: 'KGL',
          },
          {
            id: 'AG737',
            label: 'amantest01',
          },
          {
            id: 'AG742',
            label: 'amantest02',
          },
          {
            id: 'AG744',
            label: 'KL1',
          },
          {
            id: 'AG745',
            label: 'GoWR',
          },
          {
            id: 'AG746',
            label: 'Testing5Assignment',
          },
          {
            id: 'AG777',
            label: 'ss9Feb',
          },
          {
            id: 'AG778',
            label: 'NJ',
          },
          {
            id: 'AG780',
            label: 'ewewe',
          },
          {
            id: 'AG782',
            label: 'test1223',
          },
          {
            id: 'AG783',
            label: 'testin4',
          },
          {
            id: 'AG784',
            label: '19FebAssignment',
          },
          {
            id: 'AG785',
            label: 'helllNew',
          },
          {
            id: 'AG786',
            label: 'testing1222',
          },
          {
            id: 'AG788',
            label: 'testttt',
          },
          {
            id: 'AG790',
            label: 'testttt111',
          },
          {
            id: 'AG791',
            label: 'Test21926',
          },
          {
            id: 'AG792',
            label: 'Test21926Group',
            children: [
              {
                id: 'AG793',
                label: 'Test21926Assign',
              },
            ],
          },
          {
            id: 'AG794',
            label: 'hello123',
          },
          {
            id: 'AG795',
            label: 'dddddddddd',
          },
          {
            id: 'AG796',
            label: 'testing12222',
          },
          {
            id: 'AG797',
            label: '13170Data',
          },
          {
            id: 'AG800',
            label: 'newtestassiignement forcheck',
          },
          {
            id: 'AG801',
            label: 'AAssignmentex',
          },
          {
            id: 'AG802',
            label: 'TestNew',
          },
        ],
      },
    ],
  },
];

export const EditIndexDataSet = [
  {
    id: 'saved-search',
    label: 'Saved Search',
    type: 'multi',
    disabled: false,
    key: ['SSF'],
    data: [
      {
        id: 'SSF-1',
        label: 'Saved Search Folder',
        children: [
          {
            id: 'SSF-1-1',
            label: 'Saved Search',
            children: [
              {
                id: 'SSF-1-1-1',
                label: 'Saved Search',
                docCount: '10',
              },
              {
                id: 'SSF-1-1-2',
                label: 'Saved Search',
                docCount: '10',
              },
              {
                id: 'SSF-1-1-3',
                label: 'Saved Search',
                docCount: '10',
              },
              {
                id: 'SSF-1-1-4',
                label: 'Saved Search',
                docCount: '10',
              },
            ],
          },
        ],
      },
      {
        id: 'SSF-2',
        label: 'Saved Search Folder',
        children: [
          {
            id: 'SSF-2-1',
            label: 'Saved Search Folder',
            children: [
              {
                id: 'SSF-2-1-1',
                label: 'Saved Search Folder…',
                docCount: '10',
              },
              {
                id: 'SSF-2-1-2',
                label: 'Saved Search Folder…',
                docCount: '10',
              },
              {
                id: 'SSF-2-1-3',
                label: 'Saved Search Folder…',
                docCount: '10',
              },
              {
                id: 'SSF-2-1-4',
                label: 'Tag Folder…',
                docCount: '10',
              },
            ],
          },
        ],
      },
    ],
  },
  {
    id: 'tags',
    label: 'Tags',
    type: 'multi',
    disabled: false,
    key: ['TG'],
    data: [
      {
        id: 'TG-1',
        label: 'All Tags',
        children: [
          {
            id: 'TG398',
            label: 'tes1',
            docCount: '10',
          },
          {
            id: 'TG393',
            label: 'Uzi',
            docCount: '10',
          },
        ],
      },
    ],
  },
  {
    id: 'folders',
    label: 'Folders',
    type: 'multi',
    disabled: false,
    key: ['AG'],
    data: [
      {
        id: 'AG-1',
        label: 'Root',
        children: [
          {
            id: 'AG4',
            label: 'QuickBatch2',
            docCount: '10',
          },
          {
            id: 'AG12',
            label: 'Assignment18Oct25',
            docCount: '10',
          },
          {
            id: 'AG13',
            label: 'ASGN2Oct8',
            docCount: '10',
          },
          {
            id: 'AG14',
            label: 'Test',
          },
          {
            id: 'AG15',
            label: 'JLtest1022',
          },
          {
            id: 'AG16',
            label: 'testak1',
          },
          {
            id: 'AG17',
            label: 'ArrAssign24Oct01',
          },
          {
            id: 'AG18',
            label: 'ArrAssign24Oct02',
          },
          {
            id: 'AG19',
            label: 'ArrAssign24Oct03',
          },
          {
            id: 'AG20',
            label: 'ArrAssign24Oct05',
          },
          {
            id: 'AG21',
            label: 'JLAssignmentGroupCasOff',
            children: [
              {
                id: 'AG22',
                label: 'TestGroupCasOn',
                children: [
                  {
                    id: 'AG23',
                    label: 'NestedTestGroupCasOff',
                    children: [
                      {
                        id: 'AG752',
                        label: 'Test12',
                      },
                    ],
                  },
                  {
                    id: 'AG26',
                    label: 'testjen1029',
                  },
                  {
                    id: 'AG57',
                    label: 'keyword',
                    children: [
                      {
                        id: 'AG82',
                        label: '1AssignmentGroup',
                        children: [
                          {
                            id: 'AG421',
                            label: '2ndfrombulkassignwithcasacdeenabled',
                          },
                          {
                            id: 'AG422',
                            label: 'newassignemnet 2sd',
                          },
                          {
                            id: 'AG425',
                            label: 'BulkAssignmentformdocexporerwithassignid',
                          },
                          {
                            id: 'AG427',
                            label: '3rdbulkassign',
                          },
                          {
                            id: 'AG434',
                            label: 'NewAssignemntbulkassignmodechk2',
                          },
                          {
                            id: 'AG516',
                            label: 'FolderDocsFlowChk',
                          },
                          {
                            id: 'AG765',
                            label: 'New Test Assignment',
                          },
                          {
                            id: 'AG767',
                            label: 'Test123',
                          },
                          {
                            id: 'AG769',
                            label: 'asd',
                          },
                        ],
                      },
                    ],
                  },
                  {
                    id: 'AG91',
                    label: 'jl124',
                  },
                  {
                    id: 'AG747',
                    label: 'Testing 1',
                  },
                ],
              },
              {
                id: 'AG34',
                label: 'ABC test',
              },
              {
                id: 'AG92',
                label: 'jen124new',
              },
              {
                id: 'AG443',
                label: 'Assignbulk4058chk',
              },
              {
                id: 'AG450',
                label: 'asignbulk',
              },
              {
                id: 'AG727',
                label: 'NewAssignfrondc',
              },
              {
                id: 'AG772',
                label: 'jlbulkassign24',
              },
              {
                id: 'AG773',
                label: 'assignment0502',
              },
              {
                id: 'AG774',
                label: 'assignment95022',
              },
            ],
          },
          {
            id: 'AG27',
            label: 'Assignment12344',
          },
          {
            id: 'AG35',
            label: 'ASGN12Nov',
          },
          {
            id: 'AG40',
            label: 'Copy 40 Copy 5 Assignment1',
          },
          {
            id: 'AG43',
            label: 'abc tes3',
          },
          {
            id: 'AG58',
            label: 'test22',
          },
          {
            id: 'AG60',
            label: 'a7',
          },
          {
            id: 'AG61',
            label: 'TestAssignment21Nov',
          },
          {
            id: 'AG63',
            label: 'Test1',
          },
          {
            id: 'AG79',
            label: 'Test4',
          },
          {
            id: 'AG80',
            label: 'teststst',
          },
          {
            id: 'AG83',
            label: 'Test122',
          },
          {
            id: 'AG84',
            label: 'Copy 84 JLtest1022',
          },
          {
            id: 'AG85',
            label: 'Copy 85 JLtest1022',
          },
          {
            id: 'AG87',
            label: 'Ne Hemant Quick Batch',
          },
          {
            id: 'AG88',
            label: 'JLtest124',
          },
          {
            id: 'AG89',
            label: 'Copy 89 testak1',
          },
          {
            id: 'AG99',
            label: 'Copy 99 JLtest1022',
          },
          {
            id: 'AG105',
            label: 'jlquickbatch1210',
          },
          {
            id: 'AG140',
            label: 'HirenAssign',
          },
          {
            id: 'AG263',
            label: 'Test25121',
          },
          {
            id: 'AG267',
            label: 'N QB 1',
          },
          {
            id: 'AG271',
            label: 'sa',
          },
          {
            id: 'AG291',
            label: 'HTest',
          },
          {
            id: 'AG293',
            label: 'HHH',
          },
          {
            id: 'AG297',
            label: 'QHD',
          },
          {
            id: 'AG299',
            label: 'aa1',
          },
          {
            id: 'AG301',
            label: 'N QB 2',
          },
          {
            id: 'AG304',
            label: 'Hiren19dec',
          },
          {
            id: 'AG319',
            label: 'Sujata23dec',
          },
          {
            id: 'AG339',
            label: 'assign135',
          },
          {
            id: 'AG342',
            label: 'NQB23Dec',
          },
          {
            id: 'AG343',
            label: 'HirenAssign24Dec',
          },
          {
            id: 'AG346',
            label: 'Sujata2755',
          },
          {
            id: 'AG347',
            label: 'Sujata24dec',
          },
          {
            id: 'AG354',
            label: 'testTimelineAssign29Dec',
          },
          {
            id: 'AG356',
            label: 'testTimeline29New',
          },
          {
            id: 'AG359',
            label: 'AssigSelv101',
          },
          {
            id: 'AG361',
            label: 'LL',
          },
          {
            id: 'AG365',
            label: 's1',
          },
          {
            id: 'AG376',
            label: 's3',
          },
          {
            id: 'AG382',
            label: 'AssignSelv102',
          },
          {
            id: 'AG395',
            label: 's4',
          },
          {
            id: 'AG396',
            label: 's5',
          },
          {
            id: 'AG397',
            label: 's6',
          },
          {
            id: 'AG401',
            label: 's7',
          },
          {
            id: 'AG402',
            label: 's9',
          },
          {
            id: 'AG406',
            label: 'TestAssignement',
          },
          {
            id: 'AG408',
            label: 's8',
          },
          {
            id: 'AG411',
            label: 'NTest2',
          },
          {
            id: 'AG416',
            label: 'NTest4',
          },
          {
            id: 'AG418',
            label: 's10',
          },
          {
            id: 'AG424',
            label: 's11',
          },
          {
            id: 'AG436',
            label: 's12',
          },
          {
            id: 'AG438',
            label: 'bulkassignenewassignmentflow',
          },
          {
            id: 'AG444',
            label: 'Afrantest',
          },
          {
            id: 'AG446',
            label: 'qabulkassign',
          },
          {
            id: 'AG448',
            label: 'ProgressBarTest',
          },
          {
            id: 'AG451',
            label: 'CSon',
            children: [
              {
                id: 'AG348',
                label: 'Sujata24DecTech',
              },
              {
                id: 'AG454',
                label: 'Test Jan2 cson',
              },
              {
                id: 'AG462',
                label: 'TestCsonnnnnnn',
              },
              {
                id: 'AG464',
                label: 'TestCson ON',
              },
              {
                id: 'AG640',
                label: 'cson testt',
              },
              {
                id: 'AG652',
                label: 'test27',
              },
              {
                id: 'AG776',
                label: 'hd9feb',
              },
            ],
          },
          {
            id: 'AG456',
            label: 'csoff',
            children: [
              {
                id: 'AG42',
                label: 'senior',
                children: [
                  {
                    id: 'AG55',
                    label: 'junior',
                    children: [
                      {
                        id: 'AG90',
                        label: 'Dec12JL',
                        children: [
                          {
                            id: 'AG95',
                            label: 'jenassign',
                          },
                        ],
                      },
                      {
                        id: 'AG770',
                        label: 'new test assing',
                      },
                    ],
                  },
                  {
                    id: 'AG94',
                    label: 'jltest128',
                    children: [
                      {
                        id: 'AG305',
                        label: 'Sujata19Dec',
                        children: [
                          {
                            id: 'AG313',
                            label: 'TestBulkAssign22Dec',
                            children: [
                              {
                                id: 'AG442',
                                label: 'assignbulkfloew',
                              },
                            ],
                          },
                          {
                            id: 'AG344',
                            label: 'AmanGroup',
                            children: [
                              {
                                id: 'AG345',
                                label: 'TestAssgn01',
                              },
                              {
                                id: 'AG417',
                                label: 'NewAssignemnetformBulkAssign1st',
                              },
                              {
                                id: 'AG419',
                                label: 'newassignment form assignementgrid',
                              },
                              {
                                id: 'AG430',
                                label: 'NewAssignmentbulkaction',
                              },
                              {
                                id: 'AG432',
                                label: 'normalassignmentmodechk',
                              },
                              {
                                id: 'AG433',
                                label: 'finalchkbulkassign',
                              },
                              {
                                id: 'AG441',
                                label: 'NewAssignemntCreationfromBA',
                              },
                            ],
                          },
                        ],
                      },
                    ],
                  },
                ],
              },
              {
                id: 'AG457',
                label: 'Csoff assignment',
              },
              {
                id: 'AG467',
                label: 'TestCSoffff OFF',
              },
              {
                id: 'AG483',
                label: 'TestAryan',
              },
              {
                id: 'AG486',
                label: 'Testaryan9177',
              },
              {
                id: 'AG589',
                label: 'Aryan9177second',
              },
            ],
          },
          {
            id: 'AG461',
            label: 's13',
          },
          {
            id: 'AG463',
            label: 's14',
          },
          {
            id: 'AG465',
            label: 'Test11',
          },
          {
            id: 'AG470',
            label: 'sa2',
          },
          {
            id: 'AG471',
            label: 'sa4',
          },
          {
            id: 'AG472',
            label: 'ss',
          },
          {
            id: 'AG474',
            label: 'ss1',
          },
          {
            id: 'AG477',
            label: 'sa5',
          },
          {
            id: 'AG480',
            label: 'sa6',
          },
          {
            id: 'AG482',
            label: 'Testing Atryan',
          },
          {
            id: 'AG489',
            label: 'sa7',
          },
          {
            id: 'AG491',
            label: 'sa9',
          },
          {
            id: 'AG494',
            label: 'fsfsadf',
          },
          {
            id: 'AG502',
            label: 'ff',
          },
          {
            id: 'AG504',
            label: 'sa10',
          },
          {
            id: 'AG506',
            label: 'Ha2',
          },
          {
            id: 'AG511',
            label: 'Ha',
          },
          {
            id: 'AG525',
            label: 'testdec',
          },
          {
            id: 'AG528',
            label: 'Abhay Assignment',
          },
          {
            id: 'AG538',
            label: 'TestV5',
          },
          {
            id: 'AG548',
            label: 'Ha3',
          },
          {
            id: 'AG549',
            label: 'Testv6',
          },
          {
            id: 'AG557',
            label: 'TestV7',
          },
          {
            id: 'AG560',
            label: 'Testv8',
          },
          {
            id: 'AG566',
            label: 'ha4',
          },
          {
            id: 'AG567',
            label: 'HA5',
          },
          {
            id: 'AG570',
            label: 'aa3',
          },
          {
            id: 'AG571',
            label: 'aa4',
          },
          {
            id: 'AG573',
            label: 'vjtesting1',
          },
          {
            id: 'AG587',
            label: 'vjtesting2',
          },
          {
            id: 'AG592',
            label: 'vjtesting3',
          },
          {
            id: 'AG598',
            label: 'nn',
          },
          {
            id: 'AG599',
            label: 'assign789',
          },
          {
            id: 'AG600',
            label: 'jj',
          },
          {
            id: 'AG601',
            label: 'Test Jan12',
          },
          {
            id: 'AG603',
            label: 'ndn',
          },
          {
            id: 'AG604',
            label: 'testa1',
          },
          {
            id: 'AG605',
            label: 'PSAssignment',
          },
          {
            id: 'AG606',
            label: 'ps2',
          },
          {
            id: 'AG607',
            label: 'ps3',
          },
          {
            id: 'AG609',
            label: 'ps4',
          },
          {
            id: 'AG611',
            label: 'PS5',
          },
          {
            id: 'AG612',
            label: 'testv1',
          },
          {
            id: 'AG615',
            label: 'PS6',
          },
          {
            id: 'AG616',
            label: 'TEST3',
          },
          {
            id: 'AG618',
            label: 'RRR',
          },
          {
            id: 'AG620',
            label: 'vjtesting5',
          },
          {
            id: 'AG621',
            label: 'vjtesting6',
          },
          {
            id: 'AG622',
            label: 'vjtesting7',
          },
          {
            id: 'AG623',
            label: 'vjtesting8',
          },
          {
            id: 'AG624',
            label: 'vjtesting9',
          },
          {
            id: 'AG625',
            label: 'vjtesting10',
          },
          {
            id: 'AG626',
            label: 'vjtesting11',
          },
          {
            id: 'AG637',
            label: 'TT',
          },
          {
            id: 'AG639',
            label: 'cson test',
          },
          {
            id: 'AG642',
            label: 'cson check',
          },
          {
            id: 'AG644',
            label: 'NAssign',
          },
          {
            id: 'AG646',
            label: 'CNewq',
          },
          {
            id: 'AG648',
            label: 'W3',
          },
          {
            id: 'AG651',
            label: 'NightCity2077',
          },
          {
            id: 'AG653',
            label: 'test271',
          },
          {
            id: 'AG655',
            label: 'NB01',
          },
          {
            id: 'AG656',
            label: 'VFNC',
          },
          {
            id: 'AG657',
            label: 'Batch 1',
          },
          {
            id: 'AG658',
            label: 'Quick Batch 2',
          },
          {
            id: 'AG659',
            label: 'QB1',
          },
          {
            id: 'AG660',
            label: 'QB2',
          },
          {
            id: 'AG661',
            label: 'QB3',
          },
          {
            id: 'AG662',
            label: 'QB4',
          },
          {
            id: 'AG663',
            label: 'qb5',
          },
          {
            id: 'AG664',
            label: 'qb6',
          },
          {
            id: 'AG665',
            label: 'Testing2',
          },
          {
            id: 'AG666',
            label: 'test272',
          },
          {
            id: 'AG667',
            label: 'test273',
          },
          {
            id: 'AG668',
            label: 'test274',
          },
          {
            id: 'AG671',
            label: 'test275',
          },
          {
            id: 'AG673',
            label: 'test279',
          },
          {
            id: 'AG675',
            label: 'test280',
          },
          {
            id: 'AG676',
            label: 'Testing3Assignment',
          },
          {
            id: 'AG678',
            label: 'test281',
          },
          {
            id: 'AG679',
            label: 'Testing4Assignment',
          },
          {
            id: 'AG681',
            label: 'FFX',
          },
          {
            id: 'AG690',
            label: 'G of Rivia',
          },
          {
            id: 'AG694',
            label: 'Newthing',
          },
          {
            id: 'AG696',
            label: 'SD',
          },
          {
            id: 'AG698',
            label: 'S',
          },
          {
            id: 'AG704',
            label: 'A11',
          },
          {
            id: 'AG709',
            label: 'Night City 2077',
          },
          {
            id: 'AG712',
            label: 'test283',
          },
          {
            id: 'AG713',
            label: 'test285',
          },
          {
            id: 'AG715',
            label: 'test300',
          },
          {
            id: 'AG721',
            label: 'NAs77',
          },
          {
            id: 'AG728',
            label: 'RootAssign001',
          },
          {
            id: 'AG729',
            label: 'AB729',
          },
          {
            id: 'AG732',
            label: 'SM1',
          },
          {
            id: 'AG733',
            label: 'AB17',
          },
          {
            id: 'AG734',
            label: 'VK18',
          },
          {
            id: 'AG735',
            label: 'HP33',
          },
          {
            id: 'AG736',
            label: 'KGL',
          },
          {
            id: 'AG737',
            label: 'amantest01',
          },
          {
            id: 'AG742',
            label: 'amantest02',
          },
          {
            id: 'AG744',
            label: 'KL1',
          },
          {
            id: 'AG745',
            label: 'GoWR',
          },
          {
            id: 'AG746',
            label: 'Testing5Assignment',
          },
          {
            id: 'AG777',
            label: 'ss9Feb',
          },
          {
            id: 'AG778',
            label: 'NJ',
          },
          {
            id: 'AG780',
            label: 'ewewe',
          },
          {
            id: 'AG782',
            label: 'test1223',
          },
          {
            id: 'AG783',
            label: 'testin4',
          },
          {
            id: 'AG784',
            label: '19FebAssignment',
          },
          {
            id: 'AG785',
            label: 'helllNew',
          },
          {
            id: 'AG786',
            label: 'testing1222',
          },
          {
            id: 'AG788',
            label: 'testttt',
          },
          {
            id: 'AG790',
            label: 'testttt111',
          },
          {
            id: 'AG791',
            label: 'Test21926',
          },
          {
            id: 'AG792',
            label: 'Test21926Group',
            children: [
              {
                id: 'AG793',
                label: 'Test21926Assign',
              },
            ],
          },
          {
            id: 'AG794',
            label: 'hello123',
          },
          {
            id: 'AG795',
            label: 'dddddddddd',
          },
          {
            id: 'AG796',
            label: 'testing12222',
          },
          {
            id: 'AG797',
            label: '13170Data',
          },
          {
            id: 'AG800',
            label: 'newtestassiignement forcheck',
          },
          {
            id: 'AG801',
            label: 'AAssignmentex',
          },
          {
            id: 'AG802',
            label: 'TestNew',
          },
        ],
      },
    ],
  },
];
